import React, { Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Route, Routes as Switch } from 'react-router-dom';
import PropTypes from 'prop-types';
import Page from '../components/common/page';
import NotFoundPage from '../components/common/not-found';

const Home = React.lazy(() => import('../components/home/home'));

const Contact = React.lazy(() => import('../components/contact/contact'));
const ComingSoon = React.lazy(() => import('../components/common/comming-soon'));
const ChatThreads = React.lazy(() => import('../components/chat/chat-threads'));
const ChatMessagesPage = React.lazy(() => import('../components/chat/chat-messages'));
const ProductList = React.lazy(() => import('../components/products/product-list'));
const SelfProfile = React.lazy(() => import('../components/self-profile/self-profile'));
const ConnectedVendorSignup = React.lazy(() => import('../components/vendor/vendor-signup/vendor-signup'));
const VendorDashboard = React.lazy(() => import('../components/vendor/vendor-dashboard/vendor-dashboard'));
const AddProduct = React.lazy(() => import('../components/vendor/vendor-products/add-product/add-product'));
const Cart = React.lazy(() => import('../components/checkout/cart/cart'));
const OrderConfirmation = React.lazy(() => import('../components/checkout/order-confirmation/order-confirmation'));
const OrderList = React.lazy(() => import('../components/order/order-list/order-list'));
const VendorOrders = React.lazy(() => import('../components/vendor/vendor-orders/orders/orders'));
const OrderDecline = React.lazy(() => import('../components/vendor/vendor-orders/order-decline/order-decline'));
const OrderCancel = React.lazy(() => import('../components/vendor/vendor-orders/order-cancel/order-cancel'));
const ContactSupport = React.lazy(() => import('../components/vendor/vendor-orders/contact-support/contact-support'));
const AddReview = React.lazy(() => import('../components/vendor/vendor-reviews/add-review/add-review'));
const VendorProducts = React.lazy(() => import('../components/vendor/vendor-products/products/products'));
const ProductDetails = React.lazy(() => import('../components/vendor/vendor-products/product-details/product-details'));
const EditProduct = React.lazy(() => import('../components/vendor/vendor-products/edit-product/edit-product'));
const VendorReviews = React.lazy(() => import('../components/vendor/vendor-reviews/reviews/reviews'));
const TermsAndConditions = React.lazy(() => import('../components/public-pages/terms-and-conditions'));
const FAQ = React.lazy(() => import('../components/public-pages/faq'));
const AboutUs = React.lazy(() => import('../components/public-pages/about-rentovault'));
const NotificationsPage = React.lazy(() => import('../components/notification/notification-page'));
const ProductPage = React.lazy(() => import('../components/product-detail/product-page'));
const VendorProfile = React.lazy(() => import('../components/view-profile/vendor-profile'));
const LoginPage = React.lazy(() => import('../components/LoginPage/LoginPage'));
const SignupPage = React.lazy(() => import('../components/signup-page/signup-page'));
const AdminDashboard = React.lazy(() => import('../components/admin/dashboard/admin-dashboard'));
const CouponManagement = React.lazy(() => import('../components/admin/coupon/coupon-management'));
const OrderManagement = React.lazy(() => import('../components/admin/order/order-management'));
const UserManagement = React.lazy(() => import('../components/admin/user/user-management'));
const ProductManagement = React.lazy(() => import('../components/admin/product/product-management'));
const AdminChatThreads = React.lazy(() => import('../components/admin/chats/chat-list'));

function RouteFallback() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        pt: 8,
      }}
    >
      <CircularProgress />
    </Box>
  );
}

function Routes({ user }) {
  const isAuthenticated = !!user && !!user.id;

  return (
    <Suspense fallback={<RouteFallback />}>
      <Switch>
        <Route
          path="/login"
          exact
          element={(
            <Page component={LoginPage} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />

        <Route
          path="/signup"
          exact
          element={(
            <Page
              component={SignupPage}
              isAuthenticated={isAuthenticated}
              requiresLogin
              preferSignup
            />
          )}
        />

        <Route path="/" exact element={<Page component={Home} isAuthenticated={isAuthenticated} />} />
        <Route path="/contact" exact element={<Page component={Contact} isAuthenticated={isAuthenticated} />} />
        <Route path="/comingSoon" exact element={<Page component={ComingSoon} isAuthenticated={isAuthenticated} />} />
        <Route path="/cart" exact element={<Page component={Cart} isAuthenticated={isAuthenticated} />} />
        <Route path="/orders" exact element={(<Page component={OrderList} isAuthenticated={isAuthenticated} requiresLogin />)} />
        <Route path="/order-confirmation/:orderIds" exact element={(<Page component={OrderConfirmation} isAuthenticated={isAuthenticated} requiresLogin />)} />
        <Route path="/terms-and-conditions" exact element={<Page component={TermsAndConditions} isAuthenticated={isAuthenticated} />} />
        <Route path="/faq" exact element={<Page component={FAQ} isAuthenticated={isAuthenticated} />} />
        <Route path="/about-rentovault" exact element={<Page component={AboutUs} isAuthenticated={isAuthenticated} />} />
        <Route path="/notifications" exact element={<Page component={NotificationsPage} isAuthenticated={isAuthenticated} />} />
        <Route path="/product/:id/:userName/:productName" element={<Page component={ProductPage} isAuthenticated={isAuthenticated} />} />
        <Route path="/products" exact element={<Page component={ProductList} isAuthenticated={isAuthenticated} />} />
        <Route path="/tor/products" exact element={<Page component={ProductList} isAuthenticated={isAuthenticated} />} />
        <Route path="/van/products" exact element={<Page component={ProductList} isAuthenticated={isAuthenticated} />} />
        <Route path="/self-profile" exact element={<Page component={SelfProfile} isAuthenticated={isAuthenticated} requiresLogin />} />
        <Route path="/vendor-profile/:userId/:userName" exact element={<Page component={VendorProfile} isAuthenticated={isAuthenticated} />} />

        {/* Vendor related routes */}
        <Route
          path="/v/vendor-signup"
          exact
          element={(
            <Page
              component={ConnectedVendorSignup}
              isAuthenticated={isAuthenticated}
              requiresLogin
            />
          )}
        />
        <Route
          path="/v/vendor-dashboard"
          exact
          element={(
            <Page component={VendorDashboard} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />
        <Route
          path="/v/add-product"
          exact
          element={(
            <Page component={AddProduct} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />
        <Route
          path="/v/orders"
          exact
          element={(
            <Page component={VendorOrders} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />
        <Route
          path="/v/order-decline"
          exact
          element={(
            <Page component={OrderDecline} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />
        <Route
          path="/v/order-cancel"
          exact
          element={(
            <Page component={OrderCancel} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />
        <Route
          path="/v/contact-support"
          exact
          element={(
            <Page component={ContactSupport} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />
        <Route
          path="/v/review-add"
          exact
          element={(
            <Page component={AddReview} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />
        <Route
          path="/v/products"
          exact
          element={(
            <Page component={VendorProducts} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />
        <Route
          path="/v/product/:productId"
          exact
          element={(
            <Page component={ProductDetails} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />
        <Route
          path="/v/product/:productId/edit"
          exact
          element={(
            <Page component={EditProduct} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />
        <Route
          path="/v/reviews"
          exact
          element={(
            <Page component={VendorReviews} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />

        {/* Common */}
        <Route
          path="/chat"
          exact
          element={(
            <Page component={ChatThreads} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />
        <Route
          path="/chat/messages/:targetUserId"
          exact
          element={(
            <Page component={ChatMessagesPage} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />

        {/* Admin related routes */}
        <Route
          path="/a/admin-dashboard"
          exact
          element={(
            <Page component={AdminDashboard} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />

        <Route
          path="/a/coupon-management"
          exact
          element={(
            <Page component={CouponManagement} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />

        <Route
          path="/a/order-management"
          exact
          element={(
            <Page component={OrderManagement} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />

        <Route
          path="/a/user-management"
          exact
          element={(
            <Page component={UserManagement} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />

        <Route
          path="/a/product-management"
          exact
          element={(
            <Page component={ProductManagement} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />

        <Route
          path="/a/chats"
          exact
          element={(
            <Page component={AdminChatThreads} isAuthenticated={isAuthenticated} requiresLogin />
          )}
        />

        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />

      </Switch>
    </Suspense>
  );
}

Routes.propTypes = {
  user: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default Routes;
